import {
  Box,
  Button,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  VStack,
  Spinner,
  Flex,
  Divider,
} from "@chakra-ui/react";
import { X, UploadSimple, MagnifyingGlass, XCircle, WarningDiamond } from "@phosphor-icons/react";
import React from "react";
import FileUpload from "components/FileUpload";
import { sizeToString } from "api/common/fileinfo";
import fileinfoService, { apiUpload } from "api/common/fileinfo.service";
import { useQueryClient } from "react-query";
import FileDownloadButton from "components/FileDownloadButton";
import AutoComplete from "components/AutoComplete";
import { space } from "utils/text-utils";
import { dedupe } from "utils";


const MAX_FILE_SIZE_MB = Number(process.env.REACT_APP_MAX_FILE_SIZE_MB);
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const MAX_FILE_SIZE_READABLE = `${MAX_FILE_SIZE_MB}MB`;
/**
 * A small upload/browse files widget.
 * A FileInfo list + FileInfo browse-and-select + File->FileInfo upload button
 *
 * This is a UI + business logic widget -- it will upload files to the backend.
 * It won't modify questions though.
 *
 * TODO refactor so SourcesUploadPanel shares code
 *
 * Figma design: https://www.figma.com/design/uDql1qJTNBIvGnFDsOpWV4/ESG360-AIR?node-id=1426-73430&t=lsmSufHoWM3SjKoU-0
 *
 * @param {string?} fileLabel - label for the type of file. Defaults to "file"
 * @param {function} onFileInfoListChange - callback for when the file list changes
 * @param {FileInfo[]} fileInfoList - list of files to display.
 * @returns
 */
function FileListWithBrowseOrUpload({
  folder,
  tags,
  readOnly,
  minHeight,
  onFileInfoListChange,
  onExcludedListChange,
  fileLabel = "file",
  fileInfoList,
  excludedList,
  canBrowse = true,
  canUpload = true,
  canExclude = false,
  className,
  variant,
  leftIcon,
  w = "100%",
  p = 4,
  children,
  mt = 2,
}) {
  if (!fileInfoList) fileInfoList = [];

  if (!onFileInfoListChange) {
    onFileInfoListChange = (x) => console.warn("onFileInfoListChange is not defined", x);
  }

  // const toast = useToast();

  const handleRemoveFromList = (fileInfo) => {
    const allFileInfos = fileInfoList.filter((f) => f.id !== fileInfo.id);
    onFileInfoListChange(allFileInfos);
    if (excludedList?.includes(fileInfo)) {
      const newExcluded = excludedList?.filter((f) => f !== fileInfo);
      onExcludedListChange(newExcluded);
    }
  };
  /** @type {FileInfo[]} */
  const [fileInfoLoading, setFileInfoLoading] = React.useState([]);

  return (
    <VStack
      className={space("FileListWithBrowseOrUpload", className)}
      spacing={0}
      align="start"
      w={w}
      bg="gray.50"
      minHeight={minHeight}
      p={p}
      mt={mt}
    >
      <FileList
        fileInfoList={fileInfoList}
        excludedList={excludedList}
        handleRemoveFromList={!readOnly && handleRemoveFromList}
        fileInfoLoading={fileInfoLoading}
        style={{ marginBottom: "10px" }}
      />

      {fileInfoList?.length !== 0 && <Divider my={2} width="100%" border="1px" borderColor="gray.300" />}

      <AddFileWidget
        variant={variant}
        fileInfoList={fileInfoList}
        excludedList={excludedList}
        onFileInfoListChange={onFileInfoListChange}
        onExcludedListChange={onExcludedListChange}
        folder={folder}
        tags={tags}
        canBrowse={canBrowse}
        canUpload={canUpload}
        canExclude={canExclude}
        readOnly={readOnly}
        fileLabel={fileLabel}
        children={children}
        setFileInfoLoading={setFileInfoLoading}
        leftIcon={leftIcon}
        w={w}
      />
    </VStack>
  );
}

function AddFileWidget({
  fileInfoList,
  excludedList,
  onFileInfoListChange,
  onExcludedListChange,
  folder,
  tags,
  canBrowse,
  canUpload,
  canExclude,
  readOnly,
  fileLabel,
  setFileInfoLoading,
  variant = "plain",
  children,
  leftIcon,
  w,
}) {
  const [isFileBrowserOpen, _setIsFileBrowserOpen] = React.useState(false);
  const [exclude, setExclude] = React.useState(false);
  const setIsFileBrowserOpen = (open, exclude = false) => {
    _setIsFileBrowserOpen(open);
    setExclude(exclude);
  };

  const queryClient = useQueryClient();

  // sanity check / safety net
  if (!canBrowse && !canUpload && !canExclude) {
    console.warn("AddFileWidget: no browse or upload");
    return null;
  }

  // Uses Chakra Menu
  // Which does NOT re-render on state change!
  // So we have to replace the <Menu> element if we want to change the menu

  const $MenuButton = (
    <MenuButton
      as={Button}
      isDisabled={readOnly}
      variant={variant}
      w={w}
      color="whiteShade.600"
      justifyContent="center"
      leftIcon={leftIcon}
      onClick={!canUpload ? () => setIsFileBrowserOpen(true) : undefined}
    >
      {children || (
        <Box textAlign="left" fontSize="body.s">
          Add a{" "}
          <Text as="span" color="limeShade.600">
            {fileLabel}
          </Text>
        </Box>
      )}
    </MenuButton>
  );

  // File browser menu with auto-complete / select
  if (isFileBrowserOpen) {
    const fileBrowserFn = async (text) => {
      let fileinfosPage = await fileinfoService.list({ size: 1000 });
      let fileinfos = fileinfosPage.content;
      // filter by folder
      if (folder) {
        fileinfos = fileinfos.filter((f) => f.folders?.includes(folder));
      }
      // filter by text
      if (text) {
        fileinfos = fileinfos.filter((f) => JSON.stringify(f).includes(text));
      }
      // exclude already-in-the-list
      const unselected = fileinfos.filter((f) => !fileInfoList.some((fi) => fi.id === f.id));
      return unselected;
    };
    const handleSelect = (fileInfo) => {
      console.log("selected", fileInfo);
      setIsFileBrowserOpen(false);
      if (!fileInfo) return;
      if (exclude) {
        const newExcluded = [...excludedList, fileInfo];
        onExcludedListChange(newExcluded);
      } else {
        const allFileInfos = [...fileInfoList, fileInfo];
        onFileInfoListChange(allFileInfos);
      }
    };
    // TODO: The type-to-filter AutoComplete has an issue that any click causes the menu to close.
    // So using a huge visible options (1000) to force a select.
    return (
      <Menu onClose={() => setIsFileBrowserOpen(false)} isOpen={isFileBrowserOpen}>
        {$MenuButton}
        <MenuList>
          <MenuItem>
            <HStack w="full" justify="space-between">
              <Text>{exclude ? "Exclude sources" : "Browse sources"}</Text>
              <MagnifyingGlass display="inline" size={24} />
            </HStack>
          </MenuItem>
          <MenuItem>
            <AutoComplete
              onSelect={handleSelect}
              optionsProvider={fileBrowserFn}
              optionRenderer={(v) => v?.filename || v?.name || v?.id || ""}
              maxVisibleOptions={1000}
            />
          </MenuItem>
        </MenuList>
      </Menu>
    );
  } // ./isFileBrowserOpen

  // handle: upload file, then call onFileInfoListChange
  const fileInfoList_local = [...fileInfoList]; // local copy, so that handleFileUpload with multiple files can modify it several times
  const handleFileUpload = (files) => {
    console.log("handleFileUpload", files);
    if (!files || !files[0]) {
      return;
    }

    //checking for file size
    const fileArray = Array.from(files);
    const validFiles = [];
    const oversizedFiles = [];

    for (const file of fileArray) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        oversizedFiles.push(file);
      } else {
        validFiles.push(file);
      }
    }

    // ddd visible error rows for oversized files
    if (oversizedFiles.length > 0) {
      setFileInfoLoading((prev) => [
        ...prev,
        ...oversizedFiles.map((file) => ({
          id: `error_${file.name}_${file.size}_${Date.now()}`,
          filename: file.name,
          size: file.size,
          type: file.type,
          loading: false,
          error: true,
          errorMessage: `Too large (max ${MAX_FILE_SIZE_READABLE})`,
        })),
      ]);
    }
    if (validFiles.length === 0) {
      return;
    }
    // push to loading state for all files
    for (const newFile of validFiles) {
      setFileInfoLoading((prev) => [
        ...prev,
        {
          id: newFile.name,
          filename: newFile.name,
          size: newFile.size,
          type: newFile.type,
          loading: true,
        },
      ]);
    }
    // Loop over the files, uploading them one by one. This avoids breaking the max-file-size limit.
    (async () => {
      for (const newFile of validFiles) {
        const formData = new FormData();
        formData.append("file", newFile);
        if (tags) {
          formData.append("tags", tags);
        }
        if (folder) {
          formData.append("folders", [folder]);
        }
        console.log("newFile", newFile, files);

        // document.getElementById(uploadBtnId).value = null; Is this needed??
        console.log("Uploading file", newFile.name, "to folder", folder, "with tags", tags);

        try {
          const newFileInfo = await apiUpload({ data: formData });
          //
          console.log("Uploaded newFileInfo", newFileInfo);
          // setFileInfoLoading(null);

          // once successfully uploaded, remove from loading state
          setFileInfoLoading((prev) =>
            prev.filter(
              (file) => !(file.filename === newFile.name && file.size === newFile.size && file.type === newFile.type)
            )
          );

          queryClient.invalidateQueries("fileinfoService.list");
          fileInfoList_local.push(newFileInfo);
          onFileInfoListChange(fileInfoList_local);
        } catch (err) {
          console.error("Error uploading file", err);
          // set loading state to error
          setFileInfoLoading((prev) =>
            prev.map((file) => {
              if (file.filename === newFile.name && file.size === newFile.size && file.type === newFile.type) {
                return { ...file, loading: false, error: true };
              }
              return file;
            })
          );
        }
      }
    })();
  }; // ./handleFileUpload()

  // choose between upload and browse
  if (canUpload && canBrowse) {
    return (
      <Menu>
        {$MenuButton}
        <MenuList>
          {canExclude && (
            <MenuItem onClick={() => setIsFileBrowserOpen(true, true)}>
              <HStack w="full" justify="space-between">
                <Text>Exclude sources</Text>
                <XCircle display="inline" size={24} />
              </HStack>
            </MenuItem>
          )}
          {!isFileBrowserOpen && (
            <MenuItem>
              <FileUpload onUpload={handleFileUpload} w="full">
                <HStack w="full" justify="space-between">
                  <Text>Upload new source</Text>
                  <UploadSimple display="inline" size={24} />
                </HStack>
              </FileUpload>
            </MenuItem>
          )}
          <MenuItem onClick={(e) => setIsFileBrowserOpen(true)}>
            <HStack w="full" justify="space-between">
              <Text>Browse sources</Text>
              <MagnifyingGlass display="inline" size={24} />
            </HStack>
          </MenuItem>
        </MenuList>
      </Menu>
    );
  }
  // upload only
  if (canUpload) {
    return (
      <FileUpload onUpload={handleFileUpload} w="full" multiple>
        <HStack w="full" justify="space-between">
          <Text>Upload new source</Text>
          <UploadSimple display="inline" size={24} />
        </HStack>
      </FileUpload>
    );
  }
  // browse only
  return $MenuButton;
}

/**
 *
 * @param {Object} props
 * @param {FileInfo[]} props.fileInfoList - list of files to display
 * @param {function} props.handleRemoveFromList - callback for when a file is deleted
 * @returns
 */
export function FileList({ fileInfoList, excludedList, handleRemoveFromList, fileInfoLoading }) {
  if (fileInfoLoading) {
    // console.log("fileInfoLoading", fileInfoLoading);
    fileInfoList = fileInfoList.concat(fileInfoLoading);
  }
  if (!fileInfoList) fileInfoList = [];
  if (!excludedList) excludedList = [];
  const allList = dedupe([...fileInfoList, ...excludedList], "id");
  // console.log("fileInfoList", fileInfoList);
  //   console.log("alllist", allList);
  return (
    <VStack spacing={0} align="start" w="100%" className="FileList">
      {allList.map((fileInfo) => (
        <FileListItem
          key={fileInfo.id}
          fileInfo={fileInfo}
          isExcluded={excludedList.includes(fileInfo)}
          handleRemoveFromList={handleRemoveFromList}
          pages={fileInfo.pages /* HACK! */}
        />
      ))}
    </VStack>
  );
}

function FileListItem({ fileInfo, isExcluded, handleRemoveFromList, pages }) {
  const isError = !!fileInfo.error;
  const errorMessage =
    fileInfo.errorMessage || `Too large (max ${MAX_FILE_SIZE_READABLE})`;

  return (
    <HStack
      key={fileInfo.id || fileInfo.filename || fileInfo.name}
      justify="space-between"
      w="100%"
      bg={isError ? "red.50" : "gray.50"}
      py={1}
      px={2}
      borderRadius="md"
      borderWidth={isError ? "1px" : "0"}
      borderColor={isError ? "red.200" : "transparent"}
      style={{
        textDecoration: isExcluded ? "line-through" : "none",
      }}
      title={
        isError
          ? errorMessage
          : isExcluded
            ? "Excluded — AI will not use this source"
            : "Preferred source"
      }
    >
      <HStack flex={1} spacing={2} minW={0}>
        {isError ? (
          <Text fontSize="body.xs" color="red.600" noOfLines={1}>
            {(fileInfo.filename || fileInfo.name || "").toString()} — {errorMessage}
          </Text>
        ) : (
          <FileDownloadButton
            fileInfo={fileInfo}
            as={Text}
            fontSize="body.xs"
            color="darkTeal"
          >
            {fileInfo.title ||
              fileInfo.filename ||
              fileInfo.name ||
              fileInfo.id ||
              "" + fileInfo}
          </FileDownloadButton>
        )}

        {fileInfo.loading && <Spinner size="sm" />}
      </HStack>

      <HStack spacing={2} flexShrink={0}>
        {pages && (
          <Text as="span" fontSize="body.xs" color="gray.500">
            page: {pages}
          </Text>
        )}

        <Text as="span" fontSize="body.xs" color={isError ? "red.500" : "gray.500"}>
          {sizeToString(fileInfo.size)}
        </Text>

        {!isError && handleRemoveFromList && (
          <IconButton
            aria-label="Remove file from list"
            icon={<X size={20} />}
            variant="ghost"
            size="sm"
            onClick={() => handleRemoveFromList(fileInfo)}
          />
        )}
      </HStack>
    </HStack>
  );
}

export default FileListWithBrowseOrUpload;
